// Import from NPM
// -------------------------------------
import React from "react";
import localforage from "localforage";
import { Router, Route, hashHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import Loadable from "react-loadable";
import WebFont from "webfontloader";
import "semantic-ui-css/semantic.min.css";
import "config/initializers/overrule.css";

// Import from Config
// -------------------------------------
import {
    Store,
    persistStore,
    persistStoreConfig
} from "backend/storage/app.store";
import { getAppConfig } from "config/client.config";
import { appConfig } from "config/initializers/config.app";

// Import Helpers
// -------------------------------------
import { Loading } from "globals/Loading.react";

const LoadableAuthenticationContainer = Loadable({
    loader: () =>
        import(
            "backend/authentication/AuthenticationContainer.react" /* webpackChunkName: "authentication" */
        ),
    loading: Loading
});
const LoadableWelcome = Loadable({
    loader: () =>
        import(
            "/app/simulation/WelcomeContainer.react" /* webpackChunkName: "welcome" */
        ),
    loading: Loading
});
const LoadableInfo = Loadable({
    loader: () =>
        import(
            "/app/simulation/InfoContainer.react" /* webpackChunkName: "info" */
        ),
    loading: Loading
});
const LoadableCutScene = Loadable({
    loader: () =>
        import(
            "/app/simulation/CutSceneContainer.react" /* webpackChunkName: "cutscene" */
        ),
    loading: Loading
});
const LoadableInterview = Loadable({
    loader: () =>
        import(
            "/app/simulation/InterviewContainer.react" /* webpackChunkName: "interview" */
        ),
    loading: Loading
});
const LoadableProduct = Loadable({
    loader: () =>
        import(
            "/app/simulation/ProductContainer.react" /* webpackChunkName: "product" */
        ),
    loading: Loading
});
const LoadableTeam = Loadable({
    loader: () =>
        import(
            "/app/simulation/TeamContainer.react" /* webpackChunkName: "team" */
        ),
    loading: Loading
});
const LoadableAngel = Loadable({
    loader: () =>
        import(
            "/app/simulation/AngelContainer.react" /* webpackChunkName: "angel" */
        ),
    loading: Loading
});
const LoadableOperations = Loadable({
    loader: () =>
        import(
            "/app/simulation/OperationsContainer.react" /* webpackChunkName: "operations" */
        ),
    loading: Loading
});
const LoadableResult = Loadable({
    loader: () =>
        import(
            "app/simulation/ResultContainer.react" /* webpackChunkName: "result" */
        ),
    loading: Loading
});
const LoadableUserList = Loadable({
    loader: () =>
        import(
            "app/simulation/UserListContainer.react" /* webpackChunkName: "userlist" */
        ),
    loading: Loading
});
const LoadableAnalytics = Loadable({
    loader: () =>
        import(
            "app/simulation/AnalyticsContainer.react" /* webpackChunkName: "analytics" */
        ),
    loading: Loading
});

class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rehydrated: false
        };
        this.requireLogin = this.requireLogin.bind(this);
        this.alreadyLoggedIn = this.alreadyLoggedIn.bind(this);
        this.manageStore = window.manageStore = this.manageStore.bind(this);
        this.manageStore();
        WebFont.load(getAppConfig().fonts);
    }

    manageStore = purge => {
        if (purge === "purge") {
            persistStore(Store, persistStoreConfig).purge();
            localforage.removeItem("tokenHash");
        } else {
            persistStore(Store, persistStoreConfig, () => {
                let existing_user = window.localStorage.getItem(
                    "reduxPersist:auth"
                );
                if (existing_user) {
                    localforage.setItem("reduxPersist:auth", existing_user);
                }
                this.setState({ rehydrated: true });
            });
        }
    };

    requireLogin(nextState, replace, callback) {
        const unauthRoot = getAppConfig().auth.routes.unauthenticatedRoot;
        const auth = Store.getState().auth;
        if (!auth.isUserLoggedIn) {
            replace(unauthRoot);
        }
        callback();
    }

    alreadyLoggedIn(nextState, replace, callback) {
        const authRoot = appConfig.auth.routes.authenticatedRoot;
        const auth = Store.getState().auth;
        if (auth.isUserLoggedIn) {
            replace(authRoot);
        }
        callback();
    }

    render() {
        let noOverflow = {
            // overflow: "hidden auto",
            height: "100vh"
        };
        let appStyle = {
            ...getAppConfig().appStyle,
            position: "relative",
            height: "100%",
            background: "inherit"
        };

        let returnValue = (
            <div style={noOverflow}>
                <div style={appStyle}>
                    <Router history={syncHistoryWithStore(hashHistory, Store)}>
                        <Route
                            path="/login"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.alreadyLoggedIn}
                        />
                        <Route
                            path="/register"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.alreadyLoggedIn}
                        />
                        <Route
                            path="/logout"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/"
                            component={LoadableWelcome}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/info"
                            component={LoadableInfo}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/interview/:id"
                            component={LoadableInterview}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/cutscene/:id"
                            component={LoadableCutScene}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/app_builder"
                            component={LoadableProduct}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/angel"
                            component={LoadableAngel}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/team_builder"
                            component={LoadableTeam}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/operations/:id"
                            component={LoadableOperations}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/result"
                            component={LoadableResult}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/analytics"
                            component={LoadableUserList}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/analytics/:id"
                            component={LoadableAnalytics}
                            onEnter={this.requireLogin}
                        />
                    </Router>
                </div>
            </div>
        );
        if (!this.state.rehydrated) {
            returnValue = <Loading />;
        }
        return returnValue;
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/app/app.js